import { Schedule } from "types/device.types";

export interface ApiSchedule {
  on: number;
  off: number;
  days: string;
  speed: number;
}

export function timeToSeconds(timeString: string): number {
  if (timeString.includes(":")) {
    const [hours, minutes] = timeString.split(":").map(Number);
    return hours * 3600 + minutes * 60;
  }
  return parseInt(timeString, 10);
}

export function secondsToHms(total: number): string {
  const s = ((Number(total) % 86400) + 86400) % 86400;
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = Math.floor(s % 60);
  return [hours, minutes, seconds]
    .map((n) => String(n).padStart(2, "0"))
    .join(":");
}

/** Normalize days to a 7-bit Mon–Sun string for the API. */
export function normalizeDaysBits(days: string | number): string {
  const raw = String(days ?? "");
  if (/^[01]+$/.test(raw)) {
    return raw.slice(0, 7).padEnd(7, "0").slice(0, 7);
  }
  const n = Number(raw);
  if (Number.isFinite(n)) {
    return (n & 0b1111111).toString(2).padStart(7, "0");
  }
  return "1111111";
}

export function convertToApiSchedule(schedule: Schedule): ApiSchedule {
  let onSeconds: number;
  let offSeconds: number;

  if (typeof schedule.on === "string") {
    onSeconds = schedule.on.includes(":")
      ? timeToSeconds(schedule.on)
      : parseInt(schedule.on, 10);
  } else {
    onSeconds = schedule.on;
  }

  if (typeof schedule.off === "string") {
    offSeconds = schedule.off.includes(":")
      ? timeToSeconds(schedule.off)
      : parseInt(schedule.off, 10);
  } else {
    offSeconds = schedule.off;
  }

  if (isNaN(onSeconds)) onSeconds = 0;
  if (isNaN(offSeconds)) offSeconds = 0;

  return {
    on: onSeconds,
    off: offSeconds,
    days: normalizeDaysBits(schedule.days),
    speed:
      typeof schedule.speed === "string"
        ? parseInt(schedule.speed, 10)
        : schedule.speed,
  };
}

export function apiSchedulesToShadowSchedules(
  schedules: ApiSchedule[],
): Schedule[] {
  return schedules.map((schedule) => ({
    on: secondsToHms(schedule.on),
    off: secondsToHms(schedule.off),
    days: normalizeDaysBits(schedule.days),
    speed: schedule.speed,
  }));
}

export function formatTime(timeInSeconds: string): string {
  const seconds = parseInt(timeInSeconds, 10);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}`;
}
